import { CHART_TYPE } from '@tencent/chart/lib/chart-ui/interface';
import {
    LineChartConfigHelper,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/line-chart-config-helper';
import { generateCustomStyles, getShapeAttr } from '../utils/style';
import type {
    IStockChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/stock-chart.interface';
import type { IChartRendererConfig, IComboChartConfig } from '@tencent/chart/lib/chart-ui/interface';
import type { ShapeStyle } from '../interface/chart-ui.interface';
import { SpecialGeometryType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';

interface StockChartDataItem {
    category: string;
    value: number[];
}

export class StockChartConfigHelper extends LineChartConfigHelper {
    public get type(): CHART_TYPE {
        return CHART_TYPE.CANDLESTICK_CHART;
    }

    declare private highLowLinesData?: StockChartDataItem[];
    declare private upDownBarsData?: StockChartDataItem[];

    public getConfig(): IComboChartConfig {
        const chartConf = super.getConfig();
        const stockChartRenderConfigs: IChartRendererConfig[] = [];

        const [baseConfig] = chartConf.subChartRendererConfigs;
        baseConfig.type = super.type;  // type使用line的type

        // 配置高低线
        if (this.subChart.highLowLinesData) {
            const highLowLinesConf = this.getHighLowLinesConf(baseConfig);
            stockChartRenderConfigs.push(highLowLinesConf);
        }
        // 配置涨跌柱
        if (this.subChart.upDownBarsData) {
            const upDownBarsConf = this.getUpDownBarsConf(baseConfig);
            stockChartRenderConfigs.push(upDownBarsConf);
        }

        chartConf.subChartRendererConfigs.push(...stockChartRenderConfigs);
        return chartConf;
    }

    protected normalizeData() {
        super.normalizeData();

        const { highLowLinesData, upDownBarsData } = this.subChart;

        if (highLowLinesData) {
            this.highLowLinesData = highLowLinesData.map(item => ({
                ...item,
                specialGeometryType: SpecialGeometryType.highLowLine,
            }));
        }

        if (upDownBarsData) {
            this.upDownBarsData = upDownBarsData.map(item => ({
                ...item,
                specialGeometryType: SpecialGeometryType.upDownBar,
            }));
        }
    }

    protected get subChart(): IStockChartProperty {
        return this.context.getSubChartModel()?.getSubChartProperty() as IStockChartProperty;
    }

    private getHighLowLinesConf(baseRenderConf: IChartRendererConfig): IChartRendererConfig {
        const { xAxis, yAxis, xScale, yScale } = baseRenderConf;
        const shapeAttrs = getShapeAttr(this.subChart.highLowLines?.props, { filter: ['background'] });
        return {
            type: CHART_TYPE.STICK_CHART,
            // @ts-ignore
            data: this.highLowLinesData || [],
            geometryStyle: {
                ...shapeAttrs.border,
                customStyles: generateCustomStyles(shapeAttrs),
            },
            xAxis,
            yAxis,
            xScale,
            yScale,
        };
    }

    private getUpDownBarsConf(baseRenderConf: IChartRendererConfig): IChartRendererConfig {
        const { xAxis, yAxis, xScale, yScale } = baseRenderConf;
        return {
            type: CHART_TYPE.BOX_CHART,
            // @ts-ignore
            data: this.upDownBarsData,
            geometryStyle: this.createFieldMappingCfg<ShapeStyle>((range) => {
                const [start, end] = range;
                const isUpBar = end > start;

                if (isUpBar) {
                    const { fill, border, ...rest } = getShapeAttr(this.subChart.upDownBars?.upBars?.props);

                    return {
                        fill,
                        ...border,
                        customStyles: generateCustomStyles(rest),
                    };
                }

                const { fill, border, ...rest } = getShapeAttr(this.subChart.upDownBars?.downBars?.props);
                return {
                    fill,
                    ...border,
                    customStyles: generateCustomStyles(rest),
                };
            }, 'value'),
            xAxis,
            yAxis,
            xScale,
            yScale,
        };
    }
}
